import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { MealPlanModel } from '@models/meal-plan/meal-plan.model';
import { HttpService } from './http.service';

const STORAGE_KEY = 'selected_meal_plan';
const AUTO_SELECTED_KEY = 'selected_meal_plan_auto';

@Injectable({
  providedIn: 'root',
})
export class MealPlanService {
  private readonly mealPlansApi = !environment.isFullOnline
    ? `${environment.caisseApiUrl}/admissions`
    : `${environment.caisseApiUrl}/company_menu_options`;
  private readonly httpService = inject(HttpService);

  private readonly selectedMealPlan = signal<MealPlanModel | null>(this.#restore());
  readonly currentMealPlan = computed(() => this.selectedMealPlan());

  private readonly autoSelected = signal<boolean>(
    sessionStorage.getItem(AUTO_SELECTED_KEY) === 'true',
  );
  readonly isAutoSelected = computed(() => this.autoSelected());

  getMealPlans(
    userId: number,
    enterpriseId: number,
    companyId: number,
  ): Observable<MealPlanModel[]> {
    return this.httpService.get<MealPlanModel[]>(
      !environment.isFullOnline
        ? `${this.mealPlansApi}/user/${userId}/enterprise/${enterpriseId}`
        : `${this.mealPlansApi}/by_user/${userId}/${enterpriseId}`,
      { companyId },
    );
  }

  setMealPlan(mealPlan: MealPlanModel, autoSelected?: boolean): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(mealPlan));
    this.selectedMealPlan.set(mealPlan);

    if (autoSelected !== undefined) {
      sessionStorage.setItem(AUTO_SELECTED_KEY, String(autoSelected));
      this.autoSelected.set(autoSelected);
    }
  }

  clearMealPlan(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(AUTO_SELECTED_KEY);
    this.selectedMealPlan.set(null);
    this.autoSelected.set(false);
  }

  #restore(): MealPlanModel | null {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as MealPlanModel;
    } catch {
      return null;
    }
  }
}
